import { ClipboardList, Stethoscope, FileCheck, CreditCard, Truck, X, Send, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { GuidedOrderStatus, GuidedPaymentStatus } from '@/types/guidedOrder';

const STEPS: { key: string; label: string; icon: typeof Send }[] = [
  { key: 'intake_sent', label: 'Enrollment Sent', icon: Send },
  { key: 'intake', label: 'Enrollment Completed', icon: ClipboardList },
  { key: 'review', label: 'Provider Review', icon: Stethoscope },
  { key: 'rx', label: 'Rx Verified', icon: FileCheck },
  { key: 'payment', label: 'Payment', icon: CreditCard },
  { key: 'amendment', label: 'Amendment', icon: AlertTriangle },
  { key: 'fulfilled', label: 'Fulfilled', icon: Truck },
];

function getStepIndex(
  status: GuidedOrderStatus,
  paymentStatus?: GuidedPaymentStatus,
): { current: number; failedAt: number | null; warningAt: number | null } {
  const isCancelledOrExpired = status === 'cancelled' || status === 'expired';
  const isCaptureFailed = paymentStatus === 'capture_failed';

  if (isCancelledOrExpired || isCaptureFailed) {
    if (isCaptureFailed) return { current: 4, failedAt: 4, warningAt: null };
    const map: Record<string, number> = {
      awaiting_intake: 0,
      intake_completed: 1,
      mdi_submitted: 2,
      mdi_completed: 3,
      rx_verified: 3,
    };
    return { current: map[status] ?? 0, failedAt: map[status] ?? 0, warningAt: null };
  }

  // mdi_completed semantically means provider review is done and the Rx is
  // pending. Advance the tracker past "Provider Review" so the green-check
  // reflects that — same mapping used by the Nimbus flow after the
  // telehealth appointment completes.
  const map: Record<string, number> = {
    awaiting_intake: 0,
    intake_completed: 1,
    mdi_submitted: 2,
    mdi_completed: 3,
    rx_verified: 3,
    fulfilled: 6,
  };

  let current = map[status] ?? 0;

  // If rx_verified and payment captured, show payment step as done
  if (status === 'rx_verified' && paymentStatus === 'captured') {
    current = 4;
  }
  // Pending amendment — show amendment step as active (warning state)
  if (paymentStatus === 'pending_amendment') {
    return { current: 5, failedAt: null, warningAt: 5 };
  }
  if (status === 'fulfilled') {
    current = 6;
  }

  return { current, failedAt: null, warningAt: null };
}

export interface OrderLifecycleTrackerProps {
  status: GuidedOrderStatus;
  paymentStatus?: GuidedPaymentStatus;
  holdExpiresAt?: string | null;
  compact?: boolean;
}

export function OrderLifecycleTracker({
  status,
  paymentStatus,
  holdExpiresAt,
  compact = false,
}: OrderLifecycleTrackerProps) {
  const { current, failedAt, warningAt } = getStepIndex(status, paymentStatus);

  if (compact) {
    return (
      <div className="flex items-center gap-1 flex-wrap">
        {STEPS.map((step, index) => {
          // Skip amendment step if no amendment is active (not warning and not past it)
          if (step.key === 'amendment' && warningAt == null && current < index) return null;
          const Icon = step.icon;
          const isCompleted = failedAt != null ? index < failedAt : index <= current;
          const isCurrent = failedAt != null ? index === failedAt : index === current;
          const isFailed = index === failedAt;
          const isWarning = index === warningAt;
          const isFuture = failedAt != null ? index > failedAt : index > current;
          return (
            <div
              key={step.key}
              className={cn(
                'flex items-center',
                index < STEPS.length - 1 && 'mr-0.5'
              )}
            >
              <div
                className={cn(
                  'rounded-full p-0.5',
                  isFailed && 'bg-red-100 text-red-600',
                  isWarning && 'bg-orange-100 text-orange-600 animate-pulse',
                  isCompleted && !isFailed && !isWarning && 'bg-emerald-100 text-emerald-600',
                  isCurrent && !isFailed && !isWarning && 'bg-blue-100 text-blue-600 animate-pulse',
                  isFuture && !isWarning && 'bg-slate-100 text-slate-400'
                )}
              >
                {isFailed ? (
                  <X className="h-3.5 w-3.5" />
                ) : (
                  <Icon className="h-3.5 w-3.5" />
                )}
              </div>
              {index < STEPS.length - 1 && (
                <div
                  className={cn(
                    'w-2 h-0.5 mx-0.5',
                    index < current || (failedAt != null && index < failedAt)
                      ? 'bg-emerald-200'
                      : 'bg-slate-200'
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {STEPS.map((step, index) => {
        // Skip amendment step if no amendment is active
        if (step.key === 'amendment' && warningAt == null && current < index) return null;
        const Icon = step.icon;
        const isCompleted = failedAt != null ? index < failedAt : index <= current;
        const isCurrent = failedAt != null ? index === failedAt : index === current;
        const isFailed = index === failedAt;
        const isWarning = index === warningAt;
        const isFuture = failedAt != null ? index > failedAt : index > current;
        const connectorFilled =
          index < current || (failedAt != null && index < failedAt);
        return (
          <div key={step.key} className="flex items-start gap-3">
            <div className="flex flex-col items-center">
              <div
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2',
                  isFailed && 'border-red-300 bg-red-50 text-red-600',
                  isWarning && 'border-orange-400 bg-orange-50 text-orange-600 animate-pulse',
                  isCompleted && !isFailed && !isWarning && 'border-emerald-300 bg-emerald-50 text-emerald-600',
                  isCurrent && !isFailed && !isWarning && 'border-blue-400 bg-blue-50 text-blue-600 animate-pulse',
                  isFuture && !isWarning && 'border-slate-200 bg-slate-50 text-slate-400'
                )}
              >
                {isFailed ? (
                  <X className="h-4 w-4" />
                ) : (
                  <Icon className="h-4 w-4" />
                )}
              </div>
              {index < STEPS.length - 1 && (
                <div
                  className={cn(
                    'w-0.5 flex-1 min-h-[12px]',
                    connectorFilled ? 'bg-emerald-200' : 'bg-slate-200'
                  )}
                />
              )}
            </div>
            <div className="flex-1 min-w-0 pb-4">
              <p
                className={cn(
                  'text-sm font-medium',
                  isFuture && 'text-slate-500',
                  (isCompleted || isCurrent || isFailed) && 'text-slate-900'
                )}
              >
                {step.label}
              </p>
              {step.key === 'payment' && holdExpiresAt && paymentStatus === 'hold_placed' && (
                <p className="text-xs text-slate-500">
                  Hold expires {new Date(holdExpiresAt).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
